import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from "react";
import { api } from "./api.js";

export type UserRole = "admin" | "member" | "trainer";

export interface SessionUser {
  id: string;
  username: string;
  displayName: string | null;
  email: string | null;
  role: UserRole;
  status: "active" | "disabled";
  createdAt: string;
  lastLoginAt: string | null;
  onboardingComplete?: boolean;
}

interface SessionState {
  loading: boolean;
  user: SessionUser | null;
  needsBootstrap: boolean;
  /** Re-read the session + bootstrap status from the server. */
  refresh: () => Promise<void>;
  setUser: (user: SessionUser | null) => void;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const session = await api.get<{ authenticated: boolean; user?: SessionUser }>(
        "/api/auth/session"
      );
      if (session.authenticated && session.user) {
        setUser(session.user);
        setNeedsBootstrap(false);
      } else {
        setUser(null);
        const status = await api.get<{ needsBootstrap: boolean }>(
          "/api/auth/bootstrap-status"
        );
        setNeedsBootstrap(status.needsBootstrap);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
    setNeedsBootstrap(false);
  }, []);

  return (
    <SessionContext.Provider
      value={{ loading, user, needsBootstrap, refresh, setUser, logout }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within a SessionProvider");
  return ctx;
}
